import React, { useState } from 'react';
import { Button, Confirmation, TextArea, useToasts } from 'freemium-ui';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';
import Icon from 'shared/components/Icon';
import Spacer from 'shared/components/Spacer';
import { SurveyResponseType, SurveyType } from 'shared/types';
import { useAccessControl } from 'utils/access-control';
import {
  useDeleteResponseMutation,
  useResponseInvalidMutation,
  useUpdateNonActionable,
} from '../../../hooks';
import { StyledInvalidMessage } from '../style';
import { ShareResponse } from './ShareResponseButton';
import Workflow from './Workflow';

type ResponseActionsProps = {
  data?: SurveyResponseType;
  ratingType?: string;
  surveyInfo?: SurveyType;
  isResponseLoaded: boolean;
};

function ResponseActions(props: ResponseActionsProps) {
  const { data, surveyInfo, ratingType, isResponseLoaded } = props;
  const [isMessageError, setIsMessageError] = useState(false);
  const { t } = useTranslation();
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [isInvalidOpen, setIsInvalidOpen] = useState<boolean>(false);
  const [invalidMessage, setInvalidMessage] = useState<string>('');
  const history = useHistory();
  const { access } = useAccessControl();
  const [, setToast] = useToasts();
  const { mutate: markInvalid, isLoading: isInvalidLoading } =
    useResponseInvalidMutation(setToast);
  const { mutate: markNonActionable, isLoading: isNonActionableLoading } =
    useUpdateNonActionable(setToast);
  const { mutate: deleteResponse, isLoading: isDeleteLoading } =
    useDeleteResponseMutation({
      setToast,
      callback: (isSuccess: boolean) => {
        setIsDeleteOpen(false);
        if (isSuccess && surveyInfo?.id)
          history.push(`/response/${surveyInfo.id}`);
      },
    });

  const handleDeleteResponse = () => {
    deleteResponse({
      surveyId: surveyInfo?.id || '',
      responseId: data?.id,
      ratingType,
    });
  };

  const handleInvalidation = () => {
    const isMessageEntered = invalidMessage.trim().length;
    if (data?.is_invalid) {
      markInvalid({
        surveyId: surveyInfo?.id || '',
        responseId: data?.surveyserv_response_id,
        invalid: !data?.is_invalid,
        response_display_id: data?.id,
      });
    } else {
      if (isMessageEntered) {
        markInvalid({
          surveyId: surveyInfo?.id || '',
          responseId: data?.surveyserv_response_id,
          invalid: !data?.is_invalid,
          invalidMessage,
          response_display_id: data?.id,
        });
        setIsInvalidOpen(false);
        setInvalidMessage('');
      }
      setIsMessageError(!isMessageEntered);
    }
  };

  const toggleNonActionable = () => {
    markNonActionable({
      surveyId: surveyInfo?.id || '',
      responseId: data?.surveyserv_response_id,
      nonactionable: !data?.is_non_actionable,
      response_display_id: data?.id,
    });
  };

  return (
    <>
      <section className="response-action-header">
        {isResponseLoaded ? (
          <>
            {access?.includes('response:write') ? (
              <>
                <Workflow responseData={data} />
                <ShareResponse surveyResponse={data} />
                <Button
                  id="invalid-btn"
                  inline
                  type="secondary"
                  disabled={isInvalidLoading}
                  onClick={() =>
                    data?.is_invalid
                      ? handleInvalidation()
                      : setIsInvalidOpen(true)
                  }
                >
                  <Icon glyph="invalid" width="14px" height="14px" />
                  <Spacer x={0.4} />
                  {data?.is_invalid
                    ? `${t('responses.valid')}`
                    : `${t('workflow.action.action_type.mark_as_invalid')}`}
                </Button>
                <Button
                  id="non-actionable-btn"
                  type="secondary"
                  inline
                  disabled={isNonActionableLoading}
                  onClick={() => toggleNonActionable()}
                >
                  <Icon
                    glyph="non-actionable"
                    width="14px"
                    height="14px"
                    fill="none"
                  />
                  <Spacer x={0.4} />
                  {data?.is_non_actionable
                    ? `${t('responses.actionable')}`
                    : `${t('workflow.action.action_type.non_actionable')}`}
                </Button>
              </>
            ) : null}
            {access?.includes('response-edit:write') ? (
              <>
                {/* <Tooltip
                  label={
                    data?.status === 'PARTIAL_COMPLETE'
                      ? `${t('tooltip.partial_response')}`
                      : disableEdit
                      ? `${t('tooltip.not_found')}`
                      : ''
                  }
                  position="bottom"
                >
                  <Button
                    type="secondary"
                    inline
                    onClick={() => setEditOpen(true)}
                    disabled={
                      disableEdit ||
                      data?.status === 'PARTIAL_COMPLETE' ||
                      !data?.is_editable
                    }
                  >
                    <Icon glyph="edit" width="14px" height="14px" fill="none" />
                    <Spacer x={0.4} />
                    {t('edit')}
                  </Button>
                </Tooltip> */}

                <Button
                  id="response-delete-btn"
                  type="secondary"
                  inline
                  disabled={isDeleteLoading}
                  onClick={() => setIsDeleteOpen(true)}
                >
                  <Icon glyph="delete" width="14px" height="14px" fill="none" />
                  <Spacer x={0.4} />
                  {t('delete')}
                </Button>
              </>
            ) : null}
          </>
        ) : (
          <Skeleton
            containerClassName="default-loader"
            count={5}
            width={170}
            height={34}
            style={{ marginRight: '10px' }}
          />
        )}
      </section>
      <Confirmation
        isOpen={isDeleteOpen}
        onDismiss={() => setIsDeleteOpen(false)}
        title={`${t('confirm_deletion')}`}
        cancelButtonText={`${t('cancel')}`}
        confirmButtonText={`${t('confirm')}`}
        onConfirm={handleDeleteResponse}
      >
        <div style={{ width: '500px' }}>
          {t('forms.confirmation.response_irreversible')}
        </div>
      </Confirmation>
      <Confirmation
        isOpen={isInvalidOpen}
        onDismiss={() => {
          setIsInvalidOpen(false);
          setInvalidMessage('');
          setIsMessageError(false);
        }}
        title={`${t('workflow.action.action_type.mark_as_invalid')}`}
        cancelButtonText={`${t('cancel')}`}
        confirmButtonText={`${t('confirm')}`}
        onConfirm={handleInvalidation}
      >
        <StyledInvalidMessage>
          <TextArea
            label={`${t('forms.labels.reason')}`}
            placeholder={`${t('forms.placeholders.type_reason')}`}
            value={invalidMessage}
            onChange={(e: any) => setInvalidMessage(e.target.value)}
            errorText={`${t('forms.err_msg.reason_required')}`}
            isRequired
            isErrored={invalidMessage?.trim()?.length === 0 && isMessageError}
            maxLength={1500}
          />
        </StyledInvalidMessage>
      </Confirmation>
    </>
  );
}

export default ResponseActions;
